import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from "@dnd-kit/sortable";
import { Alert, Card, Center, Stack, Table as MantineTable, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { QueryKey } from "@tanstack/react-query";
import { ColumnFiltersState, Table as ReactTable, flexRender, Row } from "@tanstack/react-table";
import { CSSProperties, forwardRef, useImperativeHandle, useCallback, useMemo, useState } from "react";
import NoData from "@/components/no-data";
import LoadingRows from "@/components/table/LoadingRows";
import ResetTableButton from "@/components/table/ResetTableButton";
import { THandler } from "@/components/table/Table";
import TableHeader from "@/components/table/TableHeader";
import useQueryTable from "@/components/table/useQueryTable";
import { getQuestionariosIdsByProjeto } from "@/modules/questionarios/firestore";
import useUserStore from "@/modules/users/store";
import { getCurrentUser } from "@/services/auth";
import gruposColumns from "./Columns";
import { DEFAULT_PAGINATION, DEFAULT_SORTING, getGruposDiagnosticos } from "../../firestore";
import useGruposReorder from "../../hooks/useGruposReorder";

export const QUERY_KEY: string = "grupos-diagnostico-list";
const DRAGGING_OPACITY = 0.8;

type DraggableRowProperties = {
  row: Row<TGruposDiagnostico>;
};

const DraggableRow = ({ row }: DraggableRowProperties) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.original.id
  });

  const style: CSSProperties = {
    // Use only translate to avoid distortion (no scaleX/scaleY)
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? DRAGGING_OPACITY : 1,
    backgroundColor: isDragging ? "var(--mantine-color-blue-light)" : undefined,
    boxShadow: isDragging ? "0 4px 12px rgba(0, 0, 0, 0.15)" : undefined,
    zIndex: isDragging ? 1 : undefined
  };

  return (
    <MantineTable.Tr ref={setNodeRef} style={style}>
      {row.getVisibleCells().map((cell) => (
        <MantineTable.Td key={cell.id} {...(cell.column.id === "dragHandle" ? { ...attributes, ...listeners } : {})}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </MantineTable.Td>
      ))}
    </MantineTable.Tr>
  );
};

const GruposTable = forwardRef<THandler<TGruposDiagnostico>>((_properties, reference) => {
  const uid = getCurrentUser()?.uid;
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);
  const projeto = useUserStore((state) => state.activeProjeto);

  // Local state for optimistic reordering
  const [optimisticOrder, setOptimisticOrder] = useState<string[] | null>(null);

  // Clear optimistic state on error or success (via callbacks)
  const clearOptimisticOrder = useCallback(() => {
    setOptimisticOrder(null);
  }, []);

  const { handleReorder } = useGruposReorder({
    onError: clearOptimisticOrder,
    onSuccess: clearOptimisticOrder
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const { table, data, isLoading, error, filters, refetch } = useQueryTable<TGruposDiagnostico>({
    queryKey: [QUERY_KEY, activeEmpresaId, projeto],
    enabled: !!uid && !!activeEmpresaId,
    subjectType: "TGruposDiagnostico",
    columns: gruposColumns,
    initialState: {
      columnFilters: [],
      globalFilter: "",
      pagination: DEFAULT_PAGINATION,
      sorting: DEFAULT_SORTING
    },
    queryFunction: async ({ filters, pagination, paginationCursors }) => {
      if (!activeEmpresaId) {
        throw new Error("Empresa não selecionada");
      }

      if (!projeto) {
        throw new Error("Projeto não selecionado");
      }

      const questionariosIds = await getQuestionariosIdsByProjeto(activeEmpresaId, projeto);
      const grupoFilters: ColumnFiltersState = [{ id: "idQuestionario", value: ["in", questionariosIds] }];

      if (filters) {
        grupoFilters.push(...filters);
      }

      // Always use fixed sorting by ordemQuestionario for consistent pagination
      return await getGruposDiagnosticos(activeEmpresaId, {
        sorting: DEFAULT_SORTING,
        filters: grupoFilters,
        pagination,
        paginationCursors
      });
    }
  });

  // Get row IDs from table's row model
  const serverRows = table.getRowModel().rows;
  const serverRowIds = useMemo(() => serverRows.map((row) => row.original.id), [serverRows]);

  // Memoized key for comparing server row IDs to clear optimistic state
  const serverRowIdsKey = useMemo(() => JSON.stringify(serverRowIds), [serverRowIds]);

  const [prevServerRowIdsKey, setPrevServerRowIdsKey] = useState(serverRowIdsKey);
  if (serverRowIdsKey !== prevServerRowIdsKey) {
    setPrevServerRowIdsKey(serverRowIdsKey);
    setOptimisticOrder(null);
  }

  // Use optimistic order if available, otherwise use server order
  const rowIds = optimisticOrder ?? serverRowIds;

  // Reorder rows based on optimistic order
  const rows = useMemo(() => {
    if (!optimisticOrder) return serverRows;

    // Create a map for quick lookup
    const rowMap = new Map(serverRows.map((row) => [row.original.id, row]));

    // Return rows in optimistic order
    return optimisticOrder
      .map((id) => rowMap.get(id))
      .filter((row): row is Row<TGruposDiagnostico> => row !== undefined);
  }, [serverRows, optimisticOrder]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const activeIndex = rowIds.indexOf(active.id as string);
      const overIndex = rowIds.indexOf(over.id as string);

      if (activeIndex === -1 || overIndex === -1) {
        return;
      }

      const activeItem = rows[activeIndex]?.original;
      if (!activeItem) {
        return;
      }

      // Optimistically update local state immediately
      const newOrder = arrayMove([...rowIds], activeIndex, overIndex);
      setOptimisticOrder(newOrder);

      // Calculate new position (1-based)
      const newPosition = overIndex + 1;

      // Call backend to persist the change
      handleReorder(activeItem.idQuestionario, activeItem.id, newPosition);
    },
    [rowIds, rows, handleReorder]
  );

  useImperativeHandle<THandler<TGruposDiagnostico>, THandler<TGruposDiagnostico>>(reference, () => {
    return {
      table,
      data,
      isLoading,
      error,
      filters
    };
  }, [filters, data, error, isLoading, table]);

  /**
   *
   */
  function handleErrorClose() {
    void refetch();
  }

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size="1rem" />}
        title="Erro ao buscar grupos"
        color="red"
        withCloseButton
        onClose={handleErrorClose}
      >
        {error.message}
      </Alert>
    );
  }

  const isEmpty = !isLoading && rows.length === 0;

  return (
    <Card.Section withBorder>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <MantineTable>
          <MantineTable.Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <MantineTable.Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHeader key={header.id} header={header} />
                ))}
              </MantineTable.Tr>
            ))}
          </MantineTable.Thead>
          <MantineTable.Tbody>
            <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
              {rows.map((row) => (
                <DraggableRow key={row.original.id} row={row} />
              ))}
            </SortableContext>
            {isLoading && <LoadingRows table={table} />}
          </MantineTable.Tbody>
        </MantineTable>
      </DndContext>
      {isEmpty && !isLoading && (
        <Center m="lg">
          <NoData m="md">
            <Stack gap="xs">
              <Text c="dimmed">Nenhum Grupo Encontrado!</Text>
              <ResetTableButton table={table as ReactTable<unknown>} queryKey={QUERY_KEY as unknown as QueryKey}>
                Limpar filtros
              </ResetTableButton>
            </Stack>
          </NoData>
        </Center>
      )}
    </Card.Section>
  );
});

GruposTable.displayName = "GruposTable";

export default GruposTable;
